import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import FeatherIcon from 'feather-icons-react';
import { Button, Table, Input, Spinner, Modal, ModalBody, ModalFooter } from 'reactstrap';
import { IncidentReportPreview } from './IncidentReportPreview';
import { updateIncidentReport, sendIncidentNotification } from '../Components/FormikHelpers/Api';
import Swal from 'sweetalert2';
import { getIncidentSite } from './incidentSite';

const STATUS_OPTIONS = [
  { value: 'Open', label: 'Open', icon: 'alert-triangle', color: '#d97706', bg: '#fef3c7', border: '#fde68a' },
  { value: 'InProgress', label: 'In Progress', icon: 'activity', color: '#2563eb', bg: '#dbeafe', border: '#bfdbfe' },
  { value: 'Pending', label: 'Pending', icon: 'clock', color: '#dc2626', bg: '#fee2e2', border: '#fecaca' },
  { value: 'Resolved', label: 'Resolved', icon: 'check-circle', color: '#16a34a', bg: '#dcfce7', border: '#bbf7d0' },
];

const INVITATION_STATUS_CONFIG = {
  pending: { color: '#d97706', bg: '#fef3c7', border: '#fde68a', icon: 'clock', label: 'Pending' },
  sent: { color: '#0284c7', bg: '#f0f9ff', border: '#bae6fd', icon: 'send', label: 'Sent' },
  used: { color: '#16a34a', bg: '#dcfce7', border: '#bbf7d0', icon: 'check-circle', label: 'Used' },
};

const ROLES_CAN_DELETE = ['admin', 'company', 'manager'];
const ROLES_CAN_EDIT_STATUS = ['admin', 'company', 'manager', 'technical staff'];

const FL = ({ children, required }) => (
  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
    {children}
    {required && <span style={{ color: '#ef4444' }}>*</span>}
  </label>
);

const emptyRecipient = () => ({ firstName: '', lastName: '', email: '', managersNote: '' });

const ForwardModal = ({ isOpen, report, onConfirm, onCancel }) => {
  const [rows, setRows] = useState([emptyRecipient()]);
  const [errors, setErrors] = useState([{}]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isOpen) { setRows([emptyRecipient()]); setErrors([{}]); }
  }, [isOpen]);

  const update = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
    setErrors((prev) => prev.map((e, i) => (i === idx ? { ...e, [field]: undefined } : e)));
  };

  const addRow = () => { setRows((p) => [...p, emptyRecipient()]); setErrors((p) => [...p, {}]); };
  const removeRow = (idx) => { setRows((p) => p.filter((_, i) => i !== idx)); setErrors((p) => p.filter((_, i) => i !== idx)); };

  const validate = () => {
    const newErrors = rows.map((r) => {
      const e = {};
      if (!r.firstName.trim()) e.firstName = 'Required';
      if (!r.lastName.trim()) e.lastName = 'Required';
      if (!r.email.trim()) e.email = 'Required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email.trim())) e.email = 'Invalid email';
      return e;
    });
    setErrors(newErrors);
    return newErrors.every((e) => Object.keys(e).length === 0);
  };

  const handleForward = async () => {
    if (!validate()) return;
    setSending(true);
    try {
      const recipients = rows.map(r => ({
        firstName: r.firstName.trim(),
        lastName: r.lastName.trim(),
        email: r.email.trim(),
        managersNote: r.managersNote.trim(),
      }));
      await sendIncidentNotification(report._id, 'forward', recipients);
      Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Report forwarded successfully', showConfirmButton: false, timer: 3000 });
      onConfirm();
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: 'Forward Failed', text: err.message || 'Could not forward the report.' });
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} centered size="md" backdrop="static" style={{ zIndex: 10100 }}>
      <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>📧</span>
          <div>
            <h5 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: '#111827' }}>Forward Incident Report</h5>
            <p style={{ margin: 0, fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              Property managers &amp; staff are notified automatically. Add extra recipients below.
            </p>
          </div>
        </div>
      </div>

      <ModalBody style={{ padding: '20px 24px', maxHeight: '60vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {rows.map((row, idx) => (
            <div key={idx} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 14px 12px', position: 'relative' }}>
              {rows.length > 1 && (
                <button
                  onClick={() => removeRow(idx)}
                  style={{ position: 'absolute', top: 10, right: 10, background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 500, padding: '2px 6px', borderRadius: 6 }}
                >
                  <FeatherIcon icon="trash-2" size={13} /> Remove
                </button>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 12px', marginBottom: 10 }}>
                <div>
                  <FL required>First Name</FL>
                  <input className="ir-input" placeholder="First name" value={row.firstName} onChange={(e) => update(idx, 'firstName', e.target.value)} style={{ borderColor: errors[idx]?.firstName ? '#ef4444' : undefined, width: '100%', padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }} />
                  {errors[idx]?.firstName && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 2, display: 'block' }}>{errors[idx].firstName}</span>}
                </div>
                <div>
                  <FL required>Last Name</FL>
                  <input className="ir-input" placeholder="Last name" value={row.lastName} onChange={(e) => update(idx, 'lastName', e.target.value)} style={{ borderColor: errors[idx]?.lastName ? '#ef4444' : undefined, width: '100%', padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }} />
                  {errors[idx]?.lastName && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 2, display: 'block' }}>{errors[idx].lastName}</span>}
                </div>
              </div>

              <div style={{ marginBottom: 10 }}>
                <FL required>Email</FL>
                <input className="ir-input" type="email" placeholder="email@example.com" value={row.email} onChange={(e) => update(idx, 'email', e.target.value)} style={{ borderColor: errors[idx]?.email ? '#ef4444' : undefined, width: '100%', padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }} />
                {errors[idx]?.email && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 2, display: 'block' }}>{errors[idx].email}</span>}
              </div>
              <div style={{ marginBottom: 12 }}>
                <FL>Manager's Note</FL>
                <input className="ir-input" placeholder="Optional note for this recipient" value={row.managersNote} onChange={(e) => update(idx, 'managersNote', e.target.value)} style={{ width: '100%', padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }} />
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={addRow}
          style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: '1.5px dashed #2563EB', borderRadius: 8, color: '#2563EB', fontWeight: 600, fontSize: 13, padding: '7px 14px', cursor: 'pointer', width: '100%', justifyContent: 'center' }}
        >
          <FeatherIcon icon="plus" size={14} /> Add Another Recipient
        </button>
      </ModalBody>

      <ModalFooter style={{ padding: '14px 24px', gap: 10, borderTop: '1px solid #e2e8f0', background: '#fafbfc' }}>
        <button
          onClick={handleForward}
          disabled={sending}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 22px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 9, fontWeight: 600, fontSize: 14, cursor: 'pointer', opacity: sending ? 0.7 : 1 }}
        >
          {sending ? <Spinner size="sm" /> : <FeatherIcon icon="send" size={15} />} Confirm &amp; Forward
        </button>
        <button
          onClick={onCancel}
          disabled={sending}
          style={{ padding: '9px 18px', background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 9, color: '#374151', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
        >
          Cancel
        </button>
      </ModalFooter>
    </Modal>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(d);
  } catch { return '—'; }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }).format(d);
  } catch { return null; }
};

const getPersonDisplay = (report) => {
  const persons = report?.notification?.personsInvolved || [];
  if (!persons.length) return '—';
  const first = persons[0];
  const name = [first?.firstName, first?.lastName].filter(Boolean).join(' ').trim() || '—';
  return persons.length > 1 ? `${name} +${persons.length - 1}` : name;
};


const getNotificationDate = (report) => {
  const dateStr = report?.investigation?.notificationToPropertyManagerDateTime || report?.notification?.personsInvolved?.[0]?.notificationToPropertyManagerDateTime;
  return formatDate(dateStr);
};

const hasInvestigationData = (item) => {
  const inv = item?.investigation || {};
  return !!(
    inv.occurrenceDateTime ||
    inv.notificationToPropertyManagerDateTime ||
    (inv.statement && inv.statement.trim()) ||
    inv.witnesses?.length ||
    (inv.leaseDocument && inv.leaseDocument !== 'N/A') ||
    (inv.carInsuranceDocument && inv.carInsuranceDocument !== 'N/A') ||
    (inv.homeInsuranceDocument && inv.homeInsuranceDocument !== 'N/A') ||
    inv.attachments?.length ||
    inv.weatherConditions?.length ||
    inv.environmentConditions?.length ||
    inv.visibility?.length ||
    inv.temperatureCelsius != null ||
    inv.investigationDoneBy?.name
  );
};

const getInvitationRecipients = (invitation) => {
  const people = invitation?.prefilledPerson || [];
  if (!people.length) return '—';
  const first = people[0];
  const name = [first?.firstName, first?.lastName].filter(Boolean).join(' ').trim() || first?.email?.split('@')[0] || '—';
  return people.length > 1 ? `${name} +${people.length - 1}` : name;
};

const getStatusCfg = (status) =>
  STATUS_OPTIONS.find((s) => s.value === status) || {
    value: status, label: status, icon: 'circle', color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0',
  };

const getAllAttachments = (item) => {
  const inv = item?.investigation || {};
  const personAtts = (item?.notification?.personsInvolved || []).flatMap((p) => p?.attachments || []);
  const invAtts = inv.attachments || [];

  const isValid = (doc) => doc && doc !== 'N/A';

  const leaseDoc = isValid(inv.leaseDocument) ? [inv.leaseDocument] : [];
  const carInsurDoc = isValid(inv.carInsuranceDocument) ? [inv.carInsuranceDocument] : [];
  const homeInsurDoc = isValid(inv.homeInsuranceDocument) ? [inv.homeInsuranceDocument] : [];

  return {
    personAtts,
    invAtts,
    leaseDoc,
    carInsurDoc,
    homeInsurDoc,
    total: personAtts.length + invAtts.length + leaseDoc.length + carInsurDoc.length + homeInsurDoc.length
  };
};

const HistoryTooltipPortal = ({ anchorRef, field, history = [], onClose }) => {
  const panelRef = useRef(null);
  const [pos, setPos] = useState(null);
  const filtered = history.filter((h) => field === 'status' ? !!h.status : h.comments !== undefined && h.comments !== null).slice().reverse();

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const reposition = () => {
      const rect = anchor.getBoundingClientRect();
      const panelH = 320;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openAbove = spaceBelow < panelH + 16 && rect.top > panelH + 16;
      setPos({ left: rect.left + rect.width / 2, top: openAbove ? rect.top - 8 : rect.bottom + 8, openAbove });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => { window.removeEventListener('scroll', reposition, true); window.removeEventListener('resize', reposition); };
  }, [anchorRef]);

  useEffect(() => {
    const handleMouseDown = (e) => {
      if (anchorRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      onClose?.();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [anchorRef, onClose]);

  if (!pos) return null;
  const transform = pos.openAbove ? 'translate(-50%, -100%)' : 'translate(-50%, 0)';

  return ReactDOM.createPortal(
    <div ref={panelRef} style={{ position: 'fixed', top: pos.top, left: pos.left, transform, zIndex: 99998, background: '#1e293b', borderRadius: '12px', padding: '12px 14px', boxShadow: '0 12px 40px rgba(0,0,0,0.35)', width: '300px', maxHeight: '320px', display: 'flex', flexDirection: 'column', pointerEvents: 'auto' }}>
      <div style={{ position: 'absolute', ...(pos.openAbove ? { bottom: -7, borderTop: '7px solid #1e293b', borderBottom: 'none' } : { top: -7, borderBottom: '7px solid #1e293b', borderTop: 'none' }), left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '7px solid transparent', borderRight: '7px solid transparent' }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyBetween: 'space-between', marginBottom: '10px', flexShrink: 0 }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Change History</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '2px', display: 'flex', alignItems: 'center' }}><FeatherIcon icon="x" size={13} /></button>
      </div>
      <div style={{ overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
        {filtered.length === 0 ? <div style={{ fontSize: '13px', color: '#64748b', fontStyle: 'italic', padding: '8px 0' }}>No history yet</div> : filtered.map((entry, i) => {
          const dateStr = formatDateTime(entry.timestamp) || '—';
          const cfg = getStatusCfg(entry.status);
          return (
            <div key={i} style={{ marginBottom: i < filtered.length - 1 ? '12px' : 0, paddingBottom: i < filtered.length - 1 ? '12px' : 0, borderBottom: i < filtered.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
              {field === 'status' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(255,255,255,0.08)', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', fontWeight: 700, color: cfg.color, marginBottom: '5px' }}><FeatherIcon icon={cfg.icon} size={11} />{cfg.label}</span>}
              {field === 'comments' && <div style={{ fontSize: '13px', color: entry.comments ? '#e2e8f0' : '#64748b', fontStyle: entry.comments ? 'normal' : 'italic', marginBottom: '5px', lineHeight: 1.5, wordBreak: 'break-word' }}>{entry.comments || '(cleared)'}</div>}
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}><FeatherIcon icon="clock" size={10} color="#475569" /><span style={{ fontSize: '11px', color: '#64748b' }}>{dateStr}</span><span style={{ fontSize: '11px', color: '#334155' }}>·</span><span style={{ fontSize: '11px', color: '#64748b', textTransform: 'capitalize', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', padding: '1px 5px' }}>{entry.changedBy?.role || '—'}</span></div>
            </div>
          );
        })}
      </div>
    </div>, document.body
  );
};

const AttachmentBadge = ({ item }) => {
  const { personAtts, invAtts, leaseDoc, carInsurDoc, homeInsurDoc, total } = getAllAttachments(item);
  const [hovered, setHovered] = useState(false);
  const anchorRef = useRef(null);
  const [pos, setPos] = useState(null);

  const handleMouseEnter = () => { setHovered(true); if (anchorRef.current) { const rect = anchorRef.current.getBoundingClientRect(); setPos({ top: rect.top - 8, left: rect.left + rect.width / 2 }); } };

  if (total === 0) return <span style={{ fontSize: '12px', color: '#cbd5e1', fontStyle: 'italic' }}>None</span>;
  const rows = [
    { count: personAtts.length, icon: '📎', label: 'Person attachments' },
    { count: invAtts.length, icon: '🔍', label: 'Investigation files' },
    { count: leaseDoc.length, icon: '📄', label: 'Lease document' },
    { count: carInsurDoc.length, icon: '📋', label: 'Car Insurance' },
    { count: homeInsurDoc.length, icon: '🏠', label: 'Home Insurance' }
  ].filter((r) => r.count > 0);

  return (
    <>
      <div ref={anchorRef} onMouseEnter={handleMouseEnter} onMouseLeave={() => setHovered(false)} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: hovered ? '#e2e8f0' : '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '3px 9px', cursor: 'default', transition: 'background 0.15s' }}><FeatherIcon icon="paperclip" size={11} color="#64748b" /><span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>{total} file{total !== 1 ? 's' : ''}</span></div>
      {hovered && pos && ReactDOM.createPortal(<div style={{ position: 'fixed', top: pos.top, left: pos.left, transform: 'translate(-50%, -100%)', zIndex: 99998, background: '#1e293b', borderRadius: '10px', padding: '10px 12px', boxShadow: '0 8px 32px rgba(0,0,0,0.25)', minWidth: '190px', pointerEvents: 'none' }}><div style={{ position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #1e293b' }} /><div style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Attachments</div>{rows.map((r, i) => (<div key={i} style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: i < rows.length - 1 ? '5px' : 0 }}><span style={{ fontSize: '12px' }}>{r.icon}</span><span style={{ fontSize: '11.5px', color: '#e2e8f0', flex: 1 }}>{r.label}</span><span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8' }}>{r.count}</span></div>))}</div>, document.body)}
    </>
  );
};

const PortalDropdown = ({ anchorRef, open, onClose, children }) => {
  const [style, setStyle] = useState({});
  const portalRef = useRef(null);
  useEffect(() => { if (!open || !anchorRef.current) return; const reposition = () => { const rect = anchorRef.current?.getBoundingClientRect(); if (!rect) return; setStyle({ position: 'fixed', top: rect.bottom + 6, left: rect.left, zIndex: 99999 }); }; reposition(); window.addEventListener('scroll', reposition, true); window.addEventListener('resize', reposition); return () => { window.removeEventListener('scroll', reposition, true); window.removeEventListener('resize', reposition); }; }, [open, anchorRef]);
  useEffect(() => { if (!open) return; const handleMouseDown = (e) => { if (anchorRef.current?.contains(e.target) || portalRef.current?.contains(e.target)) return; onClose(); }; document.addEventListener('mousedown', handleMouseDown); return () => document.removeEventListener('mousedown', handleMouseDown); }, [open, anchorRef, onClose]);
  if (!open) return null;
  return ReactDOM.createPortal(<div ref={portalRef} style={style}>{children}</div>, document.body);
};

const StatusBadge = ({ status, editable, onStatusChange, saving, history = [] }) => {
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const btnRef = useRef(null);
  const historyBtnRef = useRef(null);
  if (!editable) { const inv = INVITATION_STATUS_CONFIG[status] || { color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0', icon: 'circle', label: status }; return (<span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: inv.bg, color: inv.color, border: `1px solid ${inv.border}`, borderRadius: '20px', padding: '3px 11px', fontSize: '11.5px', fontWeight: 700 }}><FeatherIcon icon={inv.icon} size={11} />{inv.label}</span>); }
  const cfg = getStatusCfg(status);
  return (
    <>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}><button ref={btnRef} onClick={() => { if (!saving) setOpen((o) => !o); }} disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: cfg.bg, color: cfg.color, border: `1.5px solid ${cfg.border}`, borderRadius: '20px', padding: '4px 11px', fontSize: '11.5px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, transition: 'all 0.15s', boxShadow: open ? `0 0 0 3px ${cfg.border}` : 'none', whiteSpace: 'nowrap' }}>{saving ? <Spinner size="sm" style={{ width: '10px', height: '10px', borderWidth: '2px' }} /> : <FeatherIcon icon={cfg.icon} size={11} />}{cfg.label}{!saving && <FeatherIcon icon="chevron-down" size={10} style={{ marginLeft: '2px', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />}</button>{history.filter(h => h.status).length > 0 && <button ref={historyBtnRef} onClick={() => setHistoryOpen(o => !o)} style={{ background: historyOpen ? '#e2e8f0' : 'transparent', border: 'none', borderRadius: '6px', padding: '3px 5px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#94a3b8' }}><FeatherIcon icon="clock" size={13} /></button>}</div>
      {historyOpen && <HistoryTooltipPortal anchorRef={historyBtnRef} field="status" history={history} onClose={() => setHistoryOpen(false)} />}
      <PortalDropdown anchorRef={btnRef} open={open} onClose={() => setOpen(false)}><div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,0.15)', overflow: 'hidden', minWidth: '150px' }}><div style={{ padding: '5px 10px 4px', fontSize: '9px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', borderBottom: '1px solid #f1f5f9' }}>Change Status</div>{STATUS_OPTIONS.map((opt) => (<button key={opt.value} onClick={() => { onStatusChange(opt.value); setOpen(false); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '7px', padding: '6px 10px', border: 'none', background: opt.value === status ? opt.bg : '#fff', color: opt.value === status ? opt.color : '#334155', fontWeight: opt.value === status ? 700 : 500, fontSize: '12px', cursor: 'pointer' }}><span style={{ width: '20px', height: '20px', borderRadius: '5px', background: opt.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FeatherIcon icon={opt.icon} size={11} color={opt.color} /></span>{opt.label}{opt.value === status && <FeatherIcon icon="check" size={11} style={{ marginLeft: 'auto' }} />}</button>))}</div></PortalDropdown>
    </>
  );
};

const InlineCommentEditor = ({ comment, onSave, saving, history = [] }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment || '');
  const [historyOpen, setHistoryOpen] = useState(false);
  const inputRef = useRef(null);
  const anchorRef = useRef(null);
  const historyBtnRef = useRef(null);
  if (editing) { return (<div style={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: '170px' }}><Input innerRef={inputRef} type="textarea" rows={2} value={draft} onChange={(e) => setDraft(e.target.value)} style={{ fontSize: '12.5px', borderRadius: '8px' }} /><div style={{ display: 'flex', gap: '5px' }}><button onClick={async () => { await onSave(draft.trim()); setEditing(false); }} disabled={saving} style={{ flex: 1, padding: '5px 0', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '12px' }}>{saving ? <Spinner size="sm" /> : 'Save'}</button><button onClick={() => setEditing(false)} style={{ padding: '5px 10px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '7px', fontSize: '12px' }}>Cancel</button></div></div>); }
  return (<><div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}><div ref={anchorRef} onClick={() => { setDraft(comment || ''); setEditing(true); setTimeout(() => inputRef.current?.focus(), 40); }} style={{ cursor: 'pointer', maxWidth: '160px', padding: '5px 7px', borderRadius: '8px', background: 'transparent' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}><span style={{ fontSize: '12.5px', color: comment ? '#334155' : '#94a3b8', fontStyle: comment ? 'normal' : 'italic' }}>{comment || 'Add comment…'}</span><FeatherIcon icon="edit-3" size={11} color="#94a3b8" style={{ marginLeft: '5px' }} /></div>{history.filter(h => h.comments).length > 0 && <button ref={historyBtnRef} onClick={() => setHistoryOpen(o => !o)} style={{ background: 'transparent', border: 'none', borderRadius: '6px', padding: '4px 5px', cursor: 'pointer', color: '#94a3b8' }}><FeatherIcon icon="clock" size={13} /></button>}</div>{historyOpen && <HistoryTooltipPortal anchorRef={historyBtnRef} field="comments" history={history} onClose={() => setHistoryOpen(false)} />}</>);
};

const ReportRow = ({ item, onDelete, onEdit, onView, userRole, mode, index, onItemUpdated }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingComment, setSavingComment] = useState(false);
  const isReport = mode === 'reports';
  const canDelete = ROLES_CAN_DELETE.includes(userRole);
  const statusHistory = item?.statusHistory || [];

  return (
    <>
      <tr onMouseEnter={(e) => (e.currentTarget.style.background = '#fafbfc')} onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
        <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: '12px', background: '#f1f5f9', borderRadius: '6px', padding: '2px 8px' }}>{isReport ? index + 1 : (item?.referenceId || '—')}</span></td>
        {isReport ? (
          <>
            <td style={{ ...tdStyle, maxWidth: '220px' }}><span title={getIncidentSite(item)} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px' }}>{getIncidentSite(item)}</span></td>
            <td style={{ ...tdStyle, fontSize: '13px' }}>{getNotificationDate(item)}</td>
            <td style={{ ...tdStyle, fontSize: '13px' }}>{getPersonDisplay(item)}</td>
            <td style={{ ...tdStyle, fontSize: '13px', color: '#64748b' }}>{formatDate(item?.createdAt)}</td>
            <td style={tdStyle}><AttachmentBadge item={item} /></td>
            <td style={tdStyle}><StatusBadge status={item?.status || 'Open'} editable={ROLES_CAN_EDIT_STATUS.includes(userRole)} onStatusChange={async (s) => { setSavingStatus(true); try { await updateIncidentReport(item._id, { status: s }); onItemUpdated?.({ ...item, status: s }); } finally { setSavingStatus(false); } }} saving={savingStatus} history={statusHistory} /></td>
            <td style={tdStyle}><InlineCommentEditor comment={item?.comments} onSave={async (c) => { setSavingComment(true); try { await updateIncidentReport(item._id, { comments: c }); onItemUpdated?.({ ...item, comments: c }); } finally { setSavingComment(false); } }} saving={savingComment} history={statusHistory} /></td>
          </>
        ) : (
          <><td style={{ ...tdStyle, fontSize: '13px' }}>{getInvitationRecipients(item)}</td><td style={{ ...tdStyle, fontSize: '13px' }}>{item?.property?.buildingName || '—'}</td><td style={{ ...tdStyle, fontSize: '13px' }}>{formatDate(item?.createdAt)}</td><td style={{ ...tdStyle, fontSize: '13px' }}>{formatDate(item?.expiresAt)}</td><td style={tdStyle}><StatusBadge status={item?.status || 'pending'} editable={false} /></td></>
        )}
        <td style={tdStyle}>
          <div style={{ display: 'flex', gap: '5px' }}>
            {isReport && (
              <>
                <Button size="sm" color="info" outline onClick={() => setPreviewOpen(true)} style={btnStyle}><FeatherIcon icon="eye" size={13} /></Button>
                {hasInvestigationData(item) && (
                  <Button size="sm" color="warning" outline onClick={() => setForwardOpen(true)} style={btnStyle} title="Forward Report">
                    <FeatherIcon icon="send" size={13} color="#ffbf53" />
                  </Button>
                )}
              </>
            )}
            {onEdit && <Button size="sm" color="primary" outline onClick={() => onEdit(item)} style={btnStyle}><FeatherIcon icon="edit-2" size={13} /></Button>}
            {canDelete && <Button size="sm" color="danger" outline onClick={() => onDelete(item)} style={btnStyle}><FeatherIcon icon="trash-2" size={13} /></Button>}
          </div>
        </td>
      </tr>
      {isReport && previewOpen && <IncidentReportPreview isOpen={previewOpen} onClose={() => setPreviewOpen(false)} reportId={item._id} userRole={userRole} />}
      {isReport && forwardOpen && <ForwardModal isOpen={forwardOpen} report={item} onConfirm={() => setForwardOpen(false)} onCancel={() => setForwardOpen(false)} />}
    </>
  );
};

const tdStyle = { padding: '12px 14px', verticalAlign: 'middle' };
const btnStyle = { padding: '4px 8px', borderRadius: '7px' };

const IncidentReportListTable = ({ items = [], onDelete, onEdit, onView, userRole = 'other', mode = 'reports', onRefresh }) => {
  const isReport = mode === 'reports';
  const [localItems, setLocalItems] = useState(items);
  useEffect(() => setLocalItems(items), [items]);
  const handleItemUpdated = (u) => setLocalItems(prev => prev.map(it => it._id === u._id ? u : it));
  const headers = isReport ? ['Serial No.', 'Site Address', 'Notification Date', 'Person Involved', 'Created', 'Attachments', 'Status', 'Comments', 'Actions'] : ['Reference ID', 'Recipients', 'Building', 'Sent Date', 'Expires', 'Status', 'Actions'];

  return (
    <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafbfc' }}>
        <div style={{ fontWeight: 700, fontSize: '14px' }}>{isReport ? 'Incident Reports' : 'Invitations'} <span style={{ background: '#e2e8f0', padding: '2px 9px', borderRadius: '20px', fontSize: '11px', color: '#64748b' }}>{localItems.length}</span></div>
        {onRefresh && <Button size="sm" color="light" onClick={onRefresh} style={{ border: '1px solid #e2e8f0' }}><FeatherIcon icon="refresh-cw" size={12} /> Refresh</Button>}
      </div>
      <div style={{ overflowX: 'auto' }}><Table style={{ margin: 0, fontSize: '13.5px' }}><thead><tr>{headers.map(h => (<th key={h} style={{ background: '#f8fafc', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', padding: '11px 14px' }}>{h}</th>))}</tr></thead><tbody>{localItems.length === 0 ? <tr><td colSpan={headers.length} style={{ textAlign: 'center', padding: '40px' }}>No items found</td></tr> : localItems.map((item, i) => (<ReportRow key={item._id || i} item={item} index={i} onDelete={onDelete} onEdit={onEdit} onView={onView} userRole={userRole} mode={mode} onItemUpdated={handleItemUpdated} />))}</tbody></Table></div>
    </div>
  );
};

export { IncidentReportListTable };